import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Breadcrumbs, Typography } from '@mui/material'
import {
  createFileRoute,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useLimitationDetailWorkflow } from '@/features/limitations'
import { ApprovalCommentDialog } from '@/features/manual-voucher-approval/ui/ApprovalCommentDialog'
import { CustomButton } from '@/shared/ui/button'
import { PageWrapper } from '@/shared/ui/page-wrapper'
import { CustomTab } from '@/shared/ui/tab'
import { CustomTabs } from '@/shared/ui/tabs'

export const Route = createFileRoute('/_auth/applications/limitations/limitation/$limitationId')({
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { limitationId } = useParams({
    from: '/_auth/applications/limitations/limitation/$limitationId',
  })

  const {
    canApproveOrReject,
    isApproving,
    isRejecting,
    handleApprove,
    handleReject,
    dialogOpen,
    dialogAction,
    dialogComment,
    setDialogComment,
    handleDialogClose,
    handleDialogConfirm,
  } = useLimitationDetailWorkflow(limitationId)

  const handleBack = () => {
    navigate({ to: '/applications/limitations' })
  }

  const getActiveTab = () => {
    if (location.pathname.includes('/allergens')) {
      return 'allergens'
    }
    if (location.pathname.includes('/history')) {
      return 'history'
    }
    if (location.pathname.includes('/common')) {
      return 'common'
    }
    return 'common'
  }

  return (
    <PageWrapper title={t('Карточка ограничения')}>
      <Box display="flex" gap={2} alignItems="center">
        <CustomButton
          size="small"
          variant="outlined"
          color="secondary"
          startIcon={<ArrowBackIos />}
          onClick={handleBack}
        >
          {t('Назад')}
        </CustomButton>
        <Breadcrumbs separator={<ArrowForwardIos sx={{ fontSize: 16 }} />}>
          <Typography color="text.secondary" key="1">
            {t('Заявки по ограничениям питания')}
          </Typography>
          <Typography key="2" color="text.primary">
            {t('Карточка ограничения')}
          </Typography>
        </Breadcrumbs>
      </Box>
      <CustomTabs value={getActiveTab()} variant="scrollable" scrollButtons="auto">
        <CustomTab
          onClick={() =>
            navigate({
              to: '/applications/limitations/limitation/$limitationId/common',
              params: { limitationId },
            })
          }
          value="common"
          label={t('Основное')}
        />
        <CustomTab
          onClick={() =>
            navigate({
              to: '/applications/limitations/limitation/$limitationId/allergens',
              params: { limitationId },
            })
          }
          value="allergens"
          label={t('Утверждённое меню с аллергенами')}
        />
        <CustomTab
          onClick={() =>
            navigate({
              to: '/applications/limitations/limitation/$limitationId/history',
              params: { limitationId },
            })
          }
          value="history"
          label={t('История')}
        />
      </CustomTabs>
      <Outlet />

      {canApproveOrReject && (
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            width: '100%',
            padding: '24px 0',
            marginTop: 'auto',
            position: 'relative',
            zIndex: 1,
            backgroundColor: 'background.paper',
          }}
        >
          <CustomButton
            type="button"
            variant="contained"
            color="primary"
            startIcon={<CheckIcon />}
            onClick={handleApprove}
            loading={isApproving}
            disabled={isApproving || isRejecting}
          >
            {t('Одобрить')}
          </CustomButton>
          <CustomButton
            type="button"
            variant="contained"
            color="secondary"
            startIcon={<CloseIcon />}
            onClick={handleReject}
            loading={isRejecting}
            disabled={isApproving || isRejecting}
          >
            {t('Отклонить')}
          </CustomButton>
        </Box>
      )}

      <ApprovalCommentDialog
        open={dialogOpen}
        action={dialogAction}
        comment={dialogComment}
        onCommentChange={setDialogComment}
        onClose={handleDialogClose}
        onConfirm={handleDialogConfirm}
        isLoading={isApproving || isRejecting}
      />
    </PageWrapper>
  )
}
